import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { checkPaymentStatus } from '../lib/paymentService';
import { useAuth } from '../context/AuthContext';

export const PaymentPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile } = useAuth();

  const stateData = (location.state as any) || {};
  const orderId = stateData.orderId || stateData.order_id || 'order_pending';
  const amount = stateData.amount || 1099;
  const planName = stateData.planName || 'Professional Host';

  const [isChecking, setIsChecking] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleCheckStatus = async () => {
    setIsChecking(true);
    setFeedback(null);
    try {
      const statusRes = await checkPaymentStatus(orderId);
      if (statusRes && statusRes.status === 'SUCCESS' && statusRes.webhook_verified === 'YES') {
        await refreshProfile?.();

        navigate('/payment-success', {
          replace: true,
          state: {
            orderId: statusRes.order_id,
            paymentId: statusRes.payment_id,
            membershipId: statusRes.membership_id,
            planName: statusRes.plan_name,
            amount: statusRes.amount,
          },
        });
        return;
      }

      if (statusRes && statusRes.status === 'FAILED') {
        navigate('/payment-failed', {
          replace: true,
          state: {
            orderId: statusRes.order_id,
            failureReason: statusRes.failure_reason,
          },
        });
        return;
      }

      setFeedback('Status: Still PENDING with bank gateway. Please wait another moment.');
    } catch {
      setFeedback('Unable to reach gateway verification server. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl border border-amber-200 shadow-xl p-6 sm:p-8 text-center space-y-6 animate-in zoom-in-95">
        
        {/* Pending Clock Icon */}
        <div className="w-20 h-20 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto shadow-inner">
          <Clock className="w-12 h-12 animate-pulse" />
        </div>

        {/* Text requested by user */}
        <div className="space-y-1.5">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">
            <Clock className="w-3.5 h-3.5" /> Verification In Progress
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
            ⏳ Payment Pending
          </h2>
          <p className="text-sm font-semibold text-amber-800">
            We are verifying your payment.
          </p>
          <p className="text-xs text-slate-500">
            Please do not make another payment.
          </p>
        </div>

        {/* Notice Card */}
        <div className="p-4 bg-amber-50/80 rounded-2xl border border-amber-200 text-left text-xs space-y-2">
          <div className="flex items-start gap-2 text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              <strong>Pending Security Rule:</strong> While payment is pending, membership remains strictly <span className="underline font-bold">inactive</span> until confirmed by bank webhook.
            </p>
          </div>
          <div className="pt-2 border-t border-amber-200/80 flex items-center justify-between text-slate-600">
            <span>Order ID:</span>
            <span className="font-mono font-semibold text-slate-800">{orderId}</span>
          </div>
          <div className="flex items-center justify-between text-slate-600">
            <span>Amount:</span>
            <span className="font-bold text-slate-800">₹{amount}</span>
          </div>
        </div>

        {feedback && (
          <p className="text-xs font-semibold text-amber-700 bg-amber-50 p-2 rounded-xl border border-amber-200">
            {feedback}
          </p>
        )}

        {/* Check Payment Status Button */}
        <div className="space-y-2.5 pt-2">
          <button
            type="button"
            disabled={isChecking}
            onClick={handleCheckStatus}
            className="w-full py-3.5 bg-gradient-to-r from-purple-700 via-purple-800 to-indigo-800 hover:from-purple-800 hover:to-indigo-900 text-white font-extrabold text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75"
          >
            <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
            <span>Check Payment Status</span>
          </button>
        </div>

      </div>
    </div>
  );
};
